import React, { useEffect, useState } from "react";
import _, { lowerCase } from "lodash";
import { useStaticQuery, graphql } from "gatsby";
import classNames from "classnames";
import { RadioGroup } from "@headlessui/react";
import {
  HomeIcon,
  BuildingLibraryIcon,
  BuildingOfficeIcon,
  XMarkIcon,
  ExclamationTriangleIcon,
} from "@heroicons/react/24/outline";

import { apiRequestToOps } from "../common/api";
import {
  caaNoFollowGroupVariants,
  capture,
  fbTrack,
  interactiveProposalGroupVariants,
} from "../common/analytics";
import { ScrollToTop } from "../misc/utils";
import { Button } from "./Button";

const minimumGdmtRateAmount = 20_000;
const minumumGdmtRateLabel = "20,000";

const normalizePhone = (phone) => {
  if (!phone) {
    return "";
  }

  // Remove whitespace and dashes
  return phone.replace(/\s+/g, "").replace(/-/g, "");
};

const getUtmValues = () => {
  const c = document.cookie
    .split("; ")
    .find((row) => row.indexOf("bright_utms=") === 0);
  if (!c) {
    return {};
  }
  const value = c.split("=")[1];
  if (!value) {
    return {};
  }

  try {
    return JSON.parse(decodeURIComponent(value));
  } catch (err) {
    console.warn(err);
    return {};
  }
};

const solutions = [
  {
    value: "residential",
    name: "Hogar",
    description: "Mauris fermentum arcu id lacus",
    icon: HomeIcon,
  },
  {
    value: "commercial",
    name: "Negocio",
    description: "Fusce eget erat sagittis odio",
    icon: BuildingLibraryIcon,
  },
  {
    value: "industrial",
    name: "Industria",
    description: "Fusce eget erat sagittis odio",
    icon: BuildingOfficeIcon,
  },
];

type WarningProps = {
  eventName?: string;
  eventProperties?: any;
  children: React.ReactNode;
};

const Warning = ({ children, eventName, eventProperties }: WarningProps) => {
  useEffect(() => {
    if (eventName) {
      capture(eventName, eventProperties);
    }
  }, [eventName]);

  return (
    <div
      className="flex flex-row bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded"
      role="alert"
    >
      <ExclamationTriangleIcon className="h-6 w-6 mr-2" />
      <div>{children}</div>
    </div>
  );
};

const CommercialWarning = ({ children }) => {
  return (
    <div className="flex flex-row items-center bg-yellow-100 border border-yellow-700 rounded-lg p-4">
      <div className="shrink-0 flex flex-row items-center justify-center h-6 w-6 bg-yellow-800 rounded-full">
        <XMarkIcon className="h-4 w-4 text-white" />
      </div>
      <div className="text-lg ml-4 font-base text-yellow-800">{children}</div>
    </div>
  );
};

const CityWarning = ({ solution, state, city }) => {
  // Don't show the warning if a state has not been selected yet
  if (!state) {
    return null;
  }

  // Don't show the warning if a valid city has been selected either
  if (city !== "other") {
    return null;
  }

  const eventName = "form-submit-invalid";
  const eventProperties = {
    reason: "no-coverage",
    solution,
    state,
  };

  return (
    <Warning eventName={eventName} eventProperties={eventProperties}>
      Desafortunadamente Bright aun no ofrece sus servicios aquí
    </Warning>
  );
};

const OwnerTypeWarning = ({ ownerType, solution, state }) => {
  if (ownerType !== "rent") {
    return null;
  }

  const eventName = "form-submit-invalid";
  const eventProperties = {
    reason: "renter",
    solution,
    state,
  };

  return (
    <Warning eventName={eventName} eventProperties={eventProperties}>
      Desafortunadamente Bright aun no ofrece sus servicios si rentas
    </Warning>
  );
};

const When = ({ isTrue, children }) => {
  if (isTrue) {
    return children;
  }

  return null;
};

type QuoteFormProps = {
  // pass the posthog feature flag, or "false"
  interactiveProposalVariant: string | boolean;

  // A warning to show if the customer selects commercial
  commercialWarning?: string;

  // The name of the form to submit
  form: string;

  // An override for how did you hear about us.  If specified hides that field in the form.
  howDidYouHearAboutUs?: string;

  channelPartnerData?: {
    channelPartnerId: string;
    slug: string;
  };

  exactBill?: boolean;

  // Array of solutions to filter out from the form
  disabledSolutions?: string[];
};

/**
 * Originally this form submitted to a Zapier catch hook, and the data was
 * transformed directly in Zapier, adding tags, setting attributes, a specific
 * format for notes, etc.
 *
 * This code replicates that functionality, and allows us to elimiante Zapier
 * from the workflow, and consequently allows our product analytics to "work"
 */
const submitFormToApi = async (
  data: any,
  experimentVariant: boolean | string,
  channelPartnerData?: any,
) => {
  // The format that we
  const attrs = {
    "customer/type": data.solution,
    "marketing/adset_name": "website",
  };

  // Since the backend doesn't have posthog integrated that well yet, replicate
  // this feature flag to the backend
  if (typeof experimentVariant === "string") {
    attrs["experiments/low_touch_pricing"] = experimentVariant;
  }

  if (data.utm_campaign) {
    attrs["marketing/campaign_name"] = data.utm_campaign;
  }
  if (data.utm_source) {
    attrs["marketing/ad_name"] = data.utm_source;
  }

  if (data.form) {
    attrs["marketing/wordpress_form"] = data.form;
  }

  const notes = `
Type: ${data.solution}
Owner Type: ${data.ownerType}
Customer Type: ${data.residentialType}
Amount: ${data.amount}
UTM Source: ${data.utm_source || ""}
UTM Campaign: ${data.utm_campaign || ""}
UTM Medium: ${data.utm_medium || ""}
    `.trim();

  const citySpecificTags = (data.tags || "").split(",");
  const tags = [
    data.utm_campaign,
    "website",
    data.form,
    ...citySpecificTags,
  ].filter((x) => x);

  // If not null, a WA msg will be sent to the lead containing this url so they
  // can review the proposal at any time
  let interactiveProposalQuery = null;

  if (caaNoFollowGroupVariants.includes(experimentVariant.toString())) {
    // The low_touch_pricing experiment specifically does _not_ utilize the help
    // of the SDR team (formerly called CAA team)
    tags.push("caa_nofollow");

    if (
      interactiveProposalGroupVariants.includes(experimentVariant.toString())
    ) {
      interactiveProposalQuery = data.interactiveProposalQuery;
    }
  }

  const payload: Record<string, any> = {
    name: data.fullName,
    contactMethod: data.phone,
    city: data.city,
    state: data.state,
    attrs,
    notes,
    isDac: data.isDac,
    customerType: data.solution,
    email: data.email,
    interactiveProposalQuery,
    ownerAnswer: data.ownerType,
    // The API expects this as a string, since it is called from Zapier as well
    // for facebook leads (and arrays are hard in that system).
    tags: tags.join(","),
  };

  if (channelPartnerData) {
    payload.channelPartnerId = channelPartnerData.channelPartnerId;
    payload.channelPartnerSlug = channelPartnerData.slug;
  }

  let url: string;
  if (process.env.NODE_ENV === "production") {
    url = "https://api.thinkbright.mx/leads/zapierLeadCreate";
  } else {
    url = "http://localhost:8089/leads/zapierLeadCreate";
  }

  const response = await apiRequestToOps("POST", url, payload);
  const token = response?.body?.token;
  if (token) {
    sessionStorage.setItem("token", token);
  }
};

export const QuoteForm = ({
  commercialWarning,
  form,
  howDidYouHearAboutUs,
  channelPartnerData,
  interactiveProposalVariant,
  exactBill,
  disabledSolutions,
}: QuoteFormProps) => {
  let experimentVariant = interactiveProposalVariant;
  const [loading, setLoading] = useState(false);

  const { allStrapiState, allStrapiCity } = useStaticQuery(graphql`
    query {
      allStrapiState(sort: { fields: [name], order: ASC }) {
        nodes {
          name
          slug
        }
      }

      allStrapiCity(sort: { fields: [name], order: ASC }) {
        nodes {
          slug
          tags
          minimum_bimonthly_business_rate
          minimum_bimonthly_residential_rate
          enable_lead_form_residential
          enable_lead_form_business
          enable_lead_form_gdmth
          name
          state {
            slug
          }
        }
      }
    }
  `);

  const states = allStrapiState.nodes;
  const cities = allStrapiCity.nodes;

  const filteredSolutionsList = disabledSolutions
    ? solutions.filter(
        (solution) => !disabledSolutions.includes(solution.value),
      )
    : solutions;

  let defaultSolution = "residential";

  if (filteredSolutionsList.length === 1) {
    defaultSolution = filteredSolutionsList[0].value;
  }

  const [formFields, setFormFields] = useState<any>({
    fields: {
      howDidYouHearAboutUs,
      solution: defaultSolution, // Set the default solution based on filteredSolutions
    },
    errors: {},
  });

  const [formStatus, setFormStatus] = useState("init");

  const handleFormChange = (field, value) => {
    let fields = { ...formFields.fields };
    fields[field] = value;
    setFormFields({ fields });
  };

  const stateOptions = () =>
    states.map((item) => (
      <option key={item.slug} value={item.slug}>
        {item.name}
      </option>
    ));

  const cityOptions = () => {
    const solution = formFields.fields["solution"];
    return cities
      .filter((city) => {
        if (solution === "commercial") {
          return city.enable_lead_form_business;
        } else if (solution === "residential") {
          return city.enable_lead_form_residential;
        } else if (solution === "industrial") {
          return city.enable_lead_form_gdmth;
        }
      })
      .filter((city) => city.state?.slug === formFields.fields["state"])
      .map((item) => (
        <option key={item.slug} value={item.slug}>
          {item.name}
        </option>
      ));
  };

  const isDacFields = () => {
    const city = cities.find((city) => city.slug === formFields.fields.city);
    const solutionSlug = formFields.fields.solution;
    const isIndustrial = solutionSlug === "industrial";

    if (!city && !isIndustrial) return null;

    let amount: string;
    let period: string = "bimestrales";

    if (solutionSlug === "commercial") {
      amount = city.minimum_bimonthly_business_rate;
    } else if (solutionSlug === "residential") {
      amount = city.minimum_bimonthly_residential_rate;
    } else if (isIndustrial) {
      // For industrial (gdmth) it does not matter by city. Just that they have
      // a bill large enough for it to make sense for us to install solar.
      amount = minumumGdmtRateLabel;
      period = "al mes";
    }

    const formattedAmout = amount ? amount.toLocaleString() : 0;
    const options = [
      {
        value: "no",
        text: `Menos de $${formattedAmout} ${period}`,
      },
      {
        value: "yes",
        text: `Más de $${formattedAmout} ${period}`,
      },
    ];

    return (
      <>
        {exactBill ? (
          <label className="block text-base text-gray-700">
            Aproximadamente ¿Cuánto pagas de luz al bimestre?
            <input
              type="text"
              placeholder="5000"
              value={formFields.fields["lastBillMxn"]}
              onChange={(e) => handleFormChange("lastBillMxn", e.target.value)}
              className="mt-1.5 text-sm md:text-base block w-full rounded-md border-gray-300 focus:border-blue-300 focus:ring focus:ring-blue-200 focus:ring-opacity-50"
            />
            {_.has(formFields, "errors.lastBillMxn") && (
              <p className="mt-2 text-sm text-red-600" id="lastBillMxn-error">
                {formFields.errors.lastBillMxn}
              </p>
            )}
          </label>
        ) : (
          <fieldset>
            <legend className="block text-base text-gray-700">
              Aproximadamente ¿Cuánto pagas de luz al bimestre?
            </legend>
            <div className="mt-4 grid grid-cols-1 gap-y-6">
              {options.map((item, index) => (
                <div key={index} className="flex items-center">
                  <label className="ml-3">
                    <input
                      type="radio"
                      name="dac"
                      className="h-4 w-4 text-blue-500 border-gray-300"
                      value={item.value}
                      onChange={(e) =>
                        handleFormChange("isDac", e.target.value)
                      }
                    />
                    <span className="text-base text-gray-700 ml-3">
                      {item.text}
                    </span>
                  </label>
                </div>
              ))}
            </div>
            {_.has(formFields, "errors.isDac") && (
              <p className="mt-2 text-sm text-red-600" id="isDac-error">
                {formFields.errors.isDac}
              </p>
            )}
          </fieldset>
        )}
      </>
    );
  };

  const ownerTypeField = () => {
    const ownerTypes = [
      {
        title: "Soy dueño/a",
        value: "owner",
      },
      {
        title: "Rento",
        value: "rent",
      },
    ];

    let label = "¿Qué opción te describe mejor? ¿Eres dueño/a o rentas?";
    if (formFields.fields["solution"] === "commercial") {
      label =
        "¿Qué opción te describe mejor? ¿Eres dueño/a de tu local o rentas?";
    }

    return (
      <label className="block text-base text-gray-700">
        {label}
        <select
          defaultValue=""
          value={formFields.fields["ownerType"]}
          onChange={(e) => handleFormChange("ownerType", e.target.value)}
          className="block text-sm md:text-base w-full mt-1.5 rounded-md border-gray-300 focus:border-blue-300 focus:ring focus:ring-blue-200 focus:ring-opacity-50"
        >
          <option selected disabled value="">
            Selecciona una opción
          </option>
          {ownerTypes.map((item, index) => (
            <option key={index} value={item.value}>
              {item.title}
            </option>
          ))}
        </select>
        {_.has(formFields, "errors.ownerType") && (
          <p className="mt-2 text-sm text-red-600" id="ownerType-error">
            {formFields.errors.ownerType}
          </p>
        )}
      </label>
    );
  };

  const residentialExtraFields = () => {
    const residentialTypes = [
      {
        title: "Casa",
        value: "home",
      },
      {
        title: "Departamento",
        value: "apartment",
      },
    ];

    if (
      !formFields.fields["solution"] ||
      formFields.fields["solution"] !== "residential"
    ) {
      return null;
    }

    return (
      <label className="block text-base text-gray-700">
        Quieres energía solar para tu...
        <select
          defaultValue=""
          value={formFields.fields["residentialType"]}
          onChange={(e) => handleFormChange("residentialType", e.target.value)}
          className="block w-full text-sm md:text-base mt-1.5 rounded-md border-gray-300 focus:border-blue-300 focus:ring focus:ring-blue-200 focus:ring-opacity-50"
        >
          <option selected disabled value="">
            Selecciona una opción
          </option>
          {residentialTypes.map((item, index) => (
            <option key={index} value={item.value}>
              {item.title}
            </option>
          ))}
          ;
        </select>
        {_.has(formFields, "errors.residentialType") && (
          <p className="mt-2 text-sm text-red-600" id="residentialType-error">
            {formFields.errors.residentialType}
          </p>
        )}
      </label>
    );
  };

  const howDidYouHearAboutUsFields = () => {
    const options = [
      {
        value: "cinepolis",
        text: `Cinépolis`,
      },
      {
        value: "correo_electronico",
        text: "Correo electrónico",
      },
      {
        value: "costco",
        text: `Costco`,
      },
      {
        value: "facebook",
        text: `Facebook`,
      },
      {
        value: "google",
        text: `Google`,
      },
      {
        value: "instagram",
        text: `Instagram`,
      },
      {
        value: "sams",
        text: `Sams`,
      },
      {
        value: "walmart",
        text: `Walmart`,
      },
      {
        value: "youtube",
        text: `YouTube`,
      },
      {
        value: "otro",
        text: `Otro`,
      },
    ];
    return (
      <>
        <label className="block text-base text-gray-700">
          ¿Cómo te enteraste de nosotros?{" "}
          <span className="text-gray-400">(opcional)</span>
          <select
            defaultValue=""
            value={formFields.fields["howDidYouHearAboutUs"]}
            onChange={(e) =>
              handleFormChange("howDidYouHearAboutUs", e.target.value)
            }
            className="text-sm md:text-base block w-full mt-1.5 rounded-md border-gray-300 focus:border-blue-300 focus:ring focus:ring-blue-200 focus:ring-opacity-50"
          >
            <option selected value="">
              Selecciona una opción
            </option>
            {options.map((item, index) => (
              <option key={index} value={item.value}>
                {item.text}
              </option>
            ))}
            ;
          </select>
          {_.has(formFields, "errors.howDidYouHearAboutUs") && (
            <p
              className="mt-2 text-sm text-red-600"
              id="howDidYouHearAboutUs-error"
            >
              {formFields.errors.howDidYouHearAboutUs}
            </p>
          )}
        </label>
      </>
    );
  };

  const formInputValidation = () => {
    let fields = formFields.fields;
    let errors = {};
    let formIsValid = true;

    // Solutions
    if (!fields["solution"]) {
      formIsValid = false;
      errors["solution"] = "Selecciona una solución";
    }

    // State
    if (!fields["state"]) {
      formIsValid = false;
      errors["state"] = "Selecciona un estado";
    }

    const isIndustrial = fields["solution"] === "industrial";
    if (
      isIndustrial &&
      fields["state"] &&
      fields["state"].includes("baja-california-norte")
    ) {
      formIsValid = false;
      errors["state"] =
        "Todavía no contamos con cobertura para industrias en este estado";
    }

    // We don't require city for GDMT leads
    if (!isIndustrial && !fields["city"]) {
      formIsValid = false;
      errors["city"] = "Selecciona una ciudad";
    }

    // Full Name
    if (!fields["fullName"]) {
      formIsValid = false;
      errors["fullName"] = "Ingresa un nombre";
    }

    // Last Bill
    if (exactBill && !fields["lastBillMxn"]) {
      formIsValid = false;
      errors["lastBillMxn"] = "Ingresa un monto";
    }

    if (exactBill && typeof fields["lastBillMxn"] !== "undefined") {
      if (!normalizePhone(fields["lastBillMxn"]).match(/^[0-9]*$/)) {
        formIsValid = false;
        errors["lastBillMxn"] = "Ingresa solo números";
      }
    }

    // isDac makes no sense for gdmt (neither for pdbt, but it only breaks here
    // if on the industrial quote page, for reasons I am not going to delve into
    // right now.)
    if (!exactBill && !isIndustrial && !_.has(fields, "isDac")) {
      formIsValid = false;
      errors["isDac"] = "Selecciona una opción";
    }

    // Email
    if (!fields["email"]) {
      formIsValid = false;
      errors["email"] = "Ingresa un correo electrónico";
    }

    // Phone
    if (!fields["phone"]) {
      formIsValid = false;
      errors["phone"] = "Ingresa un teléfono de contacto";
    }

    if (typeof fields["phone"] !== "undefined") {
      if (!normalizePhone(fields["phone"]).match(/^[0-9]*$/)) {
        formIsValid = false;
        errors["phone"] = "Ingresa solo números";
      }
    }

    if (fields["phone"] && normalizePhone(fields["phone"]).length !== 10) {
      formIsValid = false;
      errors["phone"] = "Ingresa un teléfono de 10 dígitos";
    }

    // Residential extra fields
    if (fields["solution"] == "residential" && !fields["residentialType"]) {
      formIsValid = false;
      errors["residentialType"] = "Selecciona una opción";
    }

    if (fields["solution"] == "residential" && !fields["ownerType"]) {
      formIsValid = false;
      errors["ownerType"] = "Selecciona una opción";
    }

    setFormFields({ errors: errors, fields: fields });
    return formIsValid;
  };

  const formRequirementsValidation = () => {
    let fields = formFields.fields;
    let formIsValid = true;

    // Apartment
    if (
      fields["solution"] === "residential" &&
      fields["residentialType"] === "apartment"
    ) {
      formIsValid = false;
    }

    // Rent
    if (
      fields["solution"] === "residential" &&
      fields["ownerType"] === "rent"
    ) {
      formIsValid = false;
    }

    // DAC
    if (!exactBill && fields["isDac"] === "no") {
      formIsValid = false;
    }

    if (exactBill) {
      const city = cities.find((city) => city.slug === fields["city"]);
      const isIndustrialDac =
        fields["solution"] === "industrial" &&
        fields["lastBillMxn"] >= minumumGdmtRateLabel;
      const isCommercialDac =
        fields["solution"] === "commercial" &&
        fields["lastBillMxn"] >= city.minimum_bimonthly_business_rate;
      const isResidentialDac =
        fields["solution"] === "residential" &&
        fields["lastBillMxn"] >= city.minimum_bimonthly_residential_rate;

      if (!isIndustrialDac && !isCommercialDac && !isResidentialDac) {
        formIsValid = false;
      }
    }

    // Other city is never valid
    if (fields["city"] === "other") {
      formIsValid = false;
    }

    return formIsValid;
  };

  const formSubmit = async (event) => {
    event.preventDefault();

    if (!formInputValidation()) {
      capture("form-submit-invalid");
      return;
    }

    const utmValues = getUtmValues();
    const { phone } = formFields.fields;
    const selectedCity = cities.find(
      (city) => city.slug === formFields.fields.city,
    );

    const solutionSlug = formFields.fields.solution;
    const amount = formFields.fields.lastBillMxn;

    if (solutionSlug === "industrial") {
      experimentVariant = false;
    }

    const payload = {
      ...formFields.fields,
      ...utmValues,
      form,
      phone: `+52${normalizePhone(phone)}`,
      // We set isDac to always be true to allow these leads to always be qualified
      isDac: true,
      amount,
      tags: selectedCity?.tags,
    };

    if (formRequirementsValidation()) {
      try {
        setLoading(true);

        const firstName = payload.fullName.split(" ")[0];
        const params = new URLSearchParams({
          solution: solutionSlug,
          city: payload.city,
          state: payload.state,
          lastBillMxn: payload.amount,
          ffHint: experimentVariant as string,
          firstName: lowerCase(firstName),
        });

        sessionStorage.setItem("phone", phone);

        let redirectPath = `success?${params.toString()}`;
        payload["interactiveProposalQuery"] = redirectPath;
        redirectPath = "/" + redirectPath;

        await submitFormToApi(payload, experimentVariant, channelPartnerData);

        // we would normally set the state to "successful" here,
        // but we are going to display a "success page" instead
        // We we skip this UI state entirely to avoid UI jank

        capture("form-submit-qualified", {
          solution: payload.solution,
          city: payload.city,
          state: payload.state,
          phone: payload.phone,
          residentialType: payload.residentialType,
          ownerType: payload.ownerType,
          amount: payload.amount,
          howDidYouHearAboutUs: payload.howDidYouHearAboutUs,
        });

        fbTrack("track", "FormSubmitQualified");

        // Instead of using standard analytics tracking like the above, the
        // marketing team prefers to use the page-view event for tracking this
        // particular event. Do not change this to use the Gatsby SPA
        // navigation, as it will break the pageview events the marketing team
        // is tracking.
        window.location.href = redirectPath;
      } catch (err) {
        setLoading(false);
        // Handle Error Here
        console.error(err);
      }

      ScrollToTop();
    } else {
      capture("form-submit-not-qualified", {
        solution: payload.solution,
        city: payload.city,
        state: payload.state,
        phone: payload.phone,
        residentialType: payload.residentialType,
        ownerType: payload.ownerType,
        amount: payload.amount,
        howDidYouHearAboutUs: payload.howDidYouHearAboutUs,
      });

      setFormStatus("unsuccessful");
    }
  };

  const inExperiment = interactiveProposalGroupVariants.includes(
    experimentVariant ? experimentVariant.toString() : "",
  );

  const isIndustrial = formFields.fields["solution"] === "industrial";
  return (
    <div>
      {formStatus === "init" && (
        <>
          <div className="max-w-xl flex-col items-center h-full md:h-auto">
            <h1 className="text-2xl font-bold md:text-left text-center uppercase -mx-5 md:mx-0 pb-5">
              Solicita tu propuesta de paneles solares
            </h1>
            <p className="text-base sm:mt-3 md:text-left text-center font-medium">
              Ya estás más cerca de aprovechar el poder del sol en tu techo.
              Responde unas preguntas y solicita una cotización:
            </p>
          </div>
          <form className="mt-9 flex flex-col space-y-6" onSubmit={formSubmit}>
            <RadioGroup
              value={formFields.fields["solution"]}
              onChange={(e) => {
                handleFormChange("solution", e);
              }}
            >
              <RadioGroup.Label className="block text-base text-gray-700">
                {filteredSolutionsList.length > 1 && (
                  <p>¿En dónde quieres poner paneles solares?</p>
                )}
              </RadioGroup.Label>
              <div className="flex flex-row mt-1.5 w-full">
                {filteredSolutionsList.map((item) => (
                  <RadioGroup.Option
                    key={item.name}
                    value={item.value}
                    className={({ checked }) =>
                      classNames(
                        checked
                          ? "bg-white border-gray-300"
                          : "bg-gray-50 border-gray-50",
                        "w-full block border rounded-lg bg-white px-2 py-2 cursor-pointer sm:flex sm:justify-between outline-none",
                      )
                    }
                  >
                    {({ checked }) => (
                      <>
                        <div className="flex flex-col w-full items-center">
                          <div
                            className={classNames(
                              checked ? "bg-blue-400" : "bg-gray-200",
                              "flex-shrink-0 flex items-center justify-center h-11 w-11 rounded-lg text-white sm:h-12 sm:w-12",
                            )}
                          >
                            <item.icon className="h-6 w-6" />
                          </div>
                          <div className="text-md">
                            <RadioGroup.Label
                              as="p"
                              className={classNames(
                                checked ? "text-gray-900" : "text-gray-600",
                                "",
                              )}
                            >
                              {item.name}
                            </RadioGroup.Label>
                          </div>
                        </div>
                      </>
                    )}
                  </RadioGroup.Option>
                ))}
              </div>
            </RadioGroup>

            {Boolean(commercialWarning) &&
              formFields.fields["solution"] === "commercial" && (
                <CommercialWarning>{commercialWarning}</CommercialWarning>
              )}

            <div className="flex flex-row">
              <div className={`${isIndustrial ? "w-full" : "w-1/2 mr-4"} mb-4`}>
                <label className="block text-base text-gray-700">
                  Estado
                  <select
                    defaultValue=""
                    value={formFields.fields["state"]}
                    onChange={(e) => handleFormChange("state", e.target.value)}
                    className="w-full mt-1.5 text-sm md:text-base rounded-md border-gray-300 focus:border-blue-300 focus:ring focus:ring-blue-200 focus:ring-opacity-50"
                  >
                    <option selected disabled value="">
                      Seleccionar
                    </option>
                    {stateOptions()}
                  </select>
                  {_.has(formFields, "errors.state") && (
                    <p className="mt-2 text-sm text-red-600" id="state-error">
                      {formFields.errors.state}
                    </p>
                  )}
                </label>
              </div>
              <When isTrue={!isIndustrial}>
                <div className="w-1/2">
                  <label className="block text-base text-gray-700">
                    Ciudad
                    <select
                      defaultValue=""
                      value={formFields.fields["city"]}
                      onChange={(e) => handleFormChange("city", e.target.value)}
                      className="block w-full text-sm md:text-base mt-1.5 rounded-md border-gray-300 focus:border-blue-300 focus:ring focus:ring-blue-200 focus:ring-opacity-50"
                    >
                      <option selected disabled value="">
                        Seleccionar
                      </option>
                      {cityOptions()}
                      <option key="other" value="other">
                        Otra ciudad
                      </option>
                    </select>
                    {_.has(formFields, "errors.city") && (
                      <p className="mt-2 text-sm text-red-600" id="city-error">
                        {formFields.errors.city}
                      </p>
                    )}
                  </label>
                </div>
              </When>
            </div>

            <When isTrue={!isIndustrial}>
              <CityWarning
                solution={formFields.fields["solution"]}
                state={formFields.fields["state"]}
                city={formFields.fields["city"]}
              />
            </When>

            <When
              isTrue={
                // We want to show the full form if a state has yet to be selected. Or if it is industrial the city being other does not matter
                !formFields.fields["state"] ||
                isIndustrial ||
                formFields.fields["city"] !== "other"
              }
            >
              {residentialExtraFields()}
              {ownerTypeField()}
              {isDacFields()}
              {/* SOFTWARE TO HELP ADD LABEL FOR INPUT ON CFE BILL INSTEAD OF RADIO BUTTONS*/}

              <OwnerTypeWarning
                ownerType={formFields.fields["ownerType"]}
                solution={formFields.fields["solution"]}
                state={formFields.fields["state"]}
              />
              <label className="block text-base text-gray-700">
                Nombre
                <input
                  type="text"
                  placeholder="Nombre"
                  value={formFields.fields["fullName"]}
                  onChange={(e) => handleFormChange("fullName", e.target.value)}
                  className="mt-1.5 text-sm md:text-base block w-full rounded-md border-gray-300 focus:border-blue-300 focus:ring focus:ring-blue-200 focus:ring-opacity-50"
                />
                {_.has(formFields, "errors.fullName") && (
                  <p className="mt-2 text-sm text-red-600" id="fullName-error">
                    {formFields.errors.fullName}
                  </p>
                )}
              </label>

              <label className="block text-base text-gray-700">
                Teléfono
                <div className="mt-1 flex rounded-md">
                  <span className="mt-1.5 text-sm md:text-base inline-flex items-center px-3 rounded-l-md border border-r-0 border-gray-300 bg-gray-50 text-gray-500 sm:text-sm">
                    +52
                  </span>
                  <input
                    type="tel"
                    placeholder="55 5555 5555"
                    value={formFields.fields["phone"]}
                    onChange={(e) => handleFormChange("phone", e.target.value)}
                    className="text-sm md:text-base mt-1.5 block w-full rounded-none rounded-r-md border-gray-300 focus:border-blue-300 focus:ring focus:ring-blue-200 focus:ring-opacity-50"
                  />
                </div>
                {inExperiment && (
                  <p className="mt-1 text-sm">
                    Te enviaremos tu propuesta por WhatsApp
                  </p>
                )}
                {_.has(formFields, "errors.phone") && (
                  <p className="mt-2 text-sm text-red-600" id="phone-error">
                    {formFields.errors.phone}
                  </p>
                )}
              </label>

              <label className="block text-base text-gray-700">
                Correo Electrónico
                {/*SOFTWARE HELP NEEDED HERE TO CONNECT EMAIL TO OPS :) */}
                <input
                  type="text"
                  placeholder="ejemplo@dominio.com"
                  value={formFields.fields["email"]}
                  onChange={(e) => handleFormChange("email", e.target.value)}
                  className="mt-1.5 text-sm md:text-base block w-full rounded-md border-gray-300 focus:border-blue-300 focus:ring focus:ring-blue-200 focus:ring-opacity-50"
                />
                {_.has(formFields, "errors.email") && (
                  <p className="mt-2 text-sm text-red-600" id="email-error">
                    {formFields.errors.email}
                  </p>
                )}
              </label>
              <When isTrue={formFields.fields["ownerType"] !== "rent"}>
                {!howDidYouHearAboutUs && howDidYouHearAboutUsFields()}
                <div className="w-auto flex items-center justify-center">
                  <Button type="submit" loading={loading}>
                    Enviar
                  </Button>
                </div>
              </When>
            </When>
          </form>
        </>
      )}
      {formStatus == "unsuccessful" && (
        <div className="flex flex-col justify-center items-center h-full text-center">
          <div className="flex items-center justify-center h-20 w-20 rounded-lg bg-yellow-500">
            <XMarkIcon className="h-14 w-14 text-white" />
          </div>
          <h2 className="text-3xl font-medium mt-6">Lo sentimos</h2>

          <p className="text-lg font-base mt-4 text-gray-500">
            Desafortunadamente por el momento no podemos presentarte una
            propuesta que sea más barata que tu costo actual
          </p>
        </div>
      )}
    </div>
  );
};
